import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./NotificationArrival.module.scss";

export type NotificationArrivalState = { id: string; title: string };

export const NotificationArrival = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const content = useRef<HTMLElement>(null);
  const destination = `${location.pathname}${location.search}`;
  const previousDestination = useRef(destination);
  const [arrival, setArrival] = useState<NotificationArrivalState | null>(null);

  useEffect(() => {
    if (previousDestination.current !== destination) setArrival(null);
    previousDestination.current = destination;
    const state = location.state;
    const marker = state?.notificationArrival;
    if (
      !marker ||
      typeof marker.id !== "string" ||
      typeof marker.title !== "string"
    )
      return;
    setArrival(marker);
    content.current?.focus({ preventScroll: true });
    content.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
    const remainingState = { ...state };
    delete remainingState.notificationArrival;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: remainingState,
    });
  }, [
    location.key,
    location.state,
    location.pathname,
    location.search,
    location.hash,
    destination,
    navigate,
  ]);

  useEffect(() => {
    if (!arrival) return;
    const timer = window.setTimeout(() => setArrival(null), 5000);
    return () => window.clearTimeout(timer);
  }, [arrival]);

  return (
    <main
      ref={content}
      tabIndex={-1}
      className={arrival ? styles.arrival : undefined}
      aria-label={arrival ? "Notification destination" : undefined}
    >
      {arrival && (
        <p className={styles.notice} role="status">
          Opened from notification: <strong>{arrival.title}</strong>
        </p>
      )}
      {children}
    </main>
  );
};
